import {valueOrDefault} from '../helpers/helpers';
import configs from '../configs';
import TrainTimetable from './train-timetable';

// Optional hook supplying live crowd-level data for a station code, wired up
// by the host app (e.g. map.js) from RailFeedPoller#getCrowdLevel. Left unset
// when there is no poller/data (offline dev then behaves exactly as before,
// since getCrowdDelay() below returns 0 with no provider).
let crowdDelayProvider = null;

/**
 * Registers the function used to look up the live crowd level for a station
 * code. Pass null to disable crowd-weighted dwell times again.
 * @param {Function|null} provider - (stationCode) => 'l'|'m'|'h'|null|undefined
 */
export function setCrowdDelayProvider(provider) {
    crowdDelayProvider = provider;
}

/**
 * Pure function mapping a crowd level to additional dwell time in
 * milliseconds, per configs.crowdDwellDelay. Unknown/missing levels add 0.
 * @param {string} [level] - 'l'|'m'|'h'|null|undefined
 * @returns {number} Additional dwell time in milliseconds.
 */
export function getCrowdDwellDelay(level) {
    const delays = configs.crowdDwellDelay;

    return (delays && delays[level]) || 0;
}

export default class {

    /*
        Other properties:

        timetableIndex;
        sectionIndex;
        sectionLength;
        departureStation;
        departureTime;
        arrivalStation;
        arrivalTime;
        nextDepartureTime;
        instanceID;
        coord;
        altitude;
        bearing;
        _t;
        standing;
        animationID;
        popup;
        popupVisible;
    */

    constructor(params, refs) {
        const me = this,
            fromTimetable = params instanceof TrainTimetable,
            {r, y, d, os, ds, ts, fs, nm, v, ad, delay, carComposition} = params;

        /**
         * Object type
         * $type {string}
         */
        me.type = 'train';

        /**
         * Train ID.
         * @type {string}
         */
        me.id = fromTimetable ? params.t : params.id;

        /**
         * Railway.
         * @type {Railway}
         */
        me.r = fromTimetable ? r : refs.railways.get(r);

        /**
         * Train number.
         * @type {string}
         */
        me.n = params.n;

        /**
         * Train type.
         * @type {TrainType}
         */
        me.y = fromTimetable ? y : refs.trainTypes.get(y);

        /**
         * Rail direction.
         * @type {RailDirection}
         */
        me.d = fromTimetable ? d : refs.railDirections.get(d);

        if (os) {
            /**
             * Origin stations.
             * @type {Array<Station>}
             */
            me.os = fromTimetable ? os : os.map(id => refs.stations.get(id));
        }

        if (ds) {
            /**
             * Destination stations.
             * @type {Array<Station>}
             */
            me.ds = fromTimetable ? ds : ds.map(id => refs.stations.get(id));
        }

        if (ts) {
            /**
             * To station.
             * @type {Array<Station>}
             */
            me.ts = refs.stations.get(ts);
        }

        if (fs) {
            /**
             * From station.
             * @type {Array<Station>}
             */
            me.fs = refs.stations.get(fs);
        }

        if (fromTimetable) {
            /**
             * Train timetable.
             * @type {TrainTimetable}
             */
            me.timetable = params;
        }

        if (nm) {
            /**
             * Train names.
             * @type {Array<Object>}
             */
            me.nm = nm;
        }

        if (v) {
            /**
             * Train vehicle type.
             * @type {TrainVehicleType}
             */
            me.v = fromTimetable ? v : refs.trainVehicleTypes.get(v);
        }

        if (ad) {
            /**
             * Train ad.
             * @type {Object}
             */
            me.ad = ad;
        }

        const railway = me.r;

        /**
         * Direction.
         * @type {number}
         */
        me.direction = me.d === railway.ascending ? 1 : -1;

        /**
         * Altitude.
         * @type {number}
         */
        me.altitude = railway.altitude;

        if (!isNaN(delay)) {
            /**
             * Delay.
             * @type {number}
             */
            me.delay = delay;
        }

        /**
         * Car composition.
         * @type {number}
         */
        me.carComposition = !isNaN(carComposition) ? carComposition : railway.carComposition;
    }

    update(params, refs) {
        const me = this,
            {os: _os, ds: _ds} = me,
            {y, os, ds, ts, fs, v, ad, delay, carComposition} = params,
            stations = refs.stations;

        if (y) {
            me.y = refs.trainTypes.get(y);
        }
        if (os) {
            const timetable = me.timetable;

            me.os = os.map(id => stations.get(id));

            // A potential problem of this is that connecting trains don't know if the timetable
            // reference changed.
            if (_os && os[0] !== _os[0].id && timetable) {
                const s = timetable.stations;

                for (let i = 0, ilen = s.length; i < ilen; i++) {
                    if (s[i].id === os[0]) {
                        me.timetable = timetable.clone();
                        me.timetable.stations.splice(0, i);
                        me.timetable.arrivalTimes.splice(0, i + 1, undefined);
                        me.timetable.departureTimes.splice(0, i);
                        break;
                    }
                }
            }
        }
        if (ds) {
            const timetable = me.timetable;

            me.ds = ds.map(id => stations.get(id));

            // A potential problem of this is that connecting trains don't know if the timetable
            // reference changed.
            if (_ds && ds[0] !== _ds[0].id && timetable) {
                const s = timetable.stations;

                for (let i = 0, ilen = s.length; i < ilen; i++) {
                    if (s[i].id === ds[0]) {
                        me.timetable = timetable.clone();
                        me.timetable.stations.splice(i + 1);
                        me.timetable.arrivalTimes.splice(i, Infinity, valueOrDefault(me.timetable.arrivalTimes[i], me.timetable.departureTimes[i]));
                        me.timetable.departureTimes.splice(i, Infinity, undefined);
                        break;
                    }
                }
            }
        }
        if (ts) {
            me.ts = stations.get(ts);
        }
        if (fs) {
            me.fs = stations.get(fs);
        }
        if (v) {
            me.v = refs.trainVehicleTypes.get(v);
        }
        if (ad) {
            me.ad = ad;
        }
        if (!isNaN(delay)) {
            me.delay = delay;
        }
        if (!isNaN(carComposition)) {
            me.carComposition = carComposition;
        }
    }

    /**
     * Additional dwell time (ms) to add while standing at the station this
     * train currently occupies (me.departureStation), based on live crowd
     * data. Returns 0 when no provider is registered or no code/data is
     * available, so offline/dev behavior is unchanged.
     * @returns {number} Additional dwell time in milliseconds.
     */
    getCrowdDelay() {
        const station = this.departureStation,
            code = station && station.code;

        if (!crowdDelayProvider || !code) {
            return 0;
        }

        return getCrowdDwellDelay(crowdDelayProvider(code));
    }

}
